import React, { useState } from "react";
import { format } from "date-fns";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useZustandStore from "../../../zustandStore/useZustandStore";
import CustomModal from "../../common/CustomModal";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { DatePicker } from "../../ui/date-picker";
import AdsAPIs from "../../../apiRoutes/Ads";
import { GET_AD_REQUESTS_QUERY_KEY } from "../../../utils/constants/queryKeys";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { getAgencyIdFromUser } from "../../../utils/agencyScope";

const parseIdList = (str) => {
  if (!str || typeof str !== "string") return [];
  return str
    .split(/[\s,]+/)
    .map((s) => parseInt(s, 10))
    .filter((n) => Number.isInteger(n) && n > 0);
};

const BulkDeleteByDateModal = ({ open, onClose }) => {
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [idsInput, setIdsInput] = useState("");
  const [step, setStep] = useState(1);
  const [count, setCount] = useState(0);
  const [deleteMode, setDeleteMode] = useState(null);
  const [resolvedIds, setResolvedIds] = useState([]);
  const [loadingCount, setLoadingCount] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const currentUser = useZustandStore((s) => s?.currentUser);
  const defaultAgencyId = getAgencyIdFromUser(currentUser);

  const hasIds = parseIdList(idsInput).length > 0;
  const hasDate = !!(dateFrom || dateTo);
  const isDateDisabled = hasIds;
  const isIdsDisabled = hasDate;

  const handleContinue = async () => {
    const ids = parseIdList(idsInput);
    if (ids.length > 0) {
      setResolvedIds(ids);
      setCount(ids.length);
      setDeleteMode("ids");
      setStep(2);
      return;
    }
    if (dateFrom || dateTo) {
      setLoadingCount(true);
      try {
        const res = await AdsAPIs.listAdRequests({
          axiosPrivate,
          date_from: dateFrom ? format(dateFrom, "yyyy-MM-dd") : undefined,
          date_to: dateTo ? format(dateTo, "yyyy-MM-dd") : undefined,
          requested_for_agency_id: defaultAgencyId || undefined,
          limit: 0,
          offset: 0,
        });
        setCount(res?.count ?? 0);
        setDeleteMode("date");
        setStep(2);
      } catch (_) {
        toast.error("Failed to fetch count.");
      } finally {
        setLoadingCount(false);
      }
      return;
    }
    toast.error("Enter dates or IDs.");
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      if (deleteMode === "date") {
        await AdsAPIs.deleteAdRequestsBulk({
          axiosPrivate,
          date_from: dateFrom ? format(dateFrom, "yyyy-MM-dd") : undefined,
          date_to: dateTo ? format(dateTo, "yyyy-MM-dd") : undefined,
        });
      } else {
        await AdsAPIs.deleteAdRequestsBulk({ axiosPrivate, ids: resolvedIds });
      }
      queryClient.invalidateQueries({ queryKey: GET_AD_REQUESTS_QUERY_KEY });
      toast.success(`${count} ad request(s) deleted.`);
      handleClose();
    } catch (_) {
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setDeleteMode(null);
    setDateFrom(null);
    setDateTo(null);
    setIdsInput("");
    setResolvedIds([]);
    setCount(0);
    onClose();
  };

  const handleBack = () => setStep(1);

  return (
    <CustomModal open={open} title="Bulk delete" onClose={handleClose} isButtonsRequired={false} className="md:!w-[70%] lg:!w-[50%]">
      <div className="space-y-4 py-2">
        {step === 1 && (
          <>
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1">
                <span className="text-sm font-medium">From date</span>
                <DatePicker
                  value={dateFrom}
                  onChange={setDateFrom}
                  placeholder="From date"
                  disabled={isDateDisabled}
                />
              </div>
              <div className="space-y-1">
                <span className="text-sm font-medium">To date</span>
                <DatePicker
                  value={dateTo}
                  onChange={setDateTo}
                  placeholder="To date"
                  disabled={isDateDisabled}
                />
              </div>
            </div>
            <p className="text-sm font-medium text-muted-foreground">OR</p>
            <div className="space-y-1">
              <Input
                placeholder="Enter IDs"
                value={idsInput}
                onChange={(e) => setIdsInput(e.target.value)}
                disabled={isIdsDisabled}
                className="max-w-xs"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleContinue} disabled={loadingCount}>
                {loadingCount ? "Loading…" : "Continue"}
              </Button>
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <p className="text-sm">
              <span className="font-medium">{count}</span> ad request{count !== 1 ? "s" : ""} will be deleted. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleBack} disabled={deleting}>
                Back
              </Button>
              <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
                {deleting ? "Deleting…" : "Delete"}
              </Button>
            </div>
          </>
        )}
      </div>
    </CustomModal>
  );
};

export default BulkDeleteByDateModal;
